import AdminHomepage from "./AdminHomepage";
import FaviconField from "@/components/admin/FaviconField";
import React, { useEffect, useState, useRef } from "react";
import api, { mediaUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Upload, Plus, Trash2, GripVertical, Save, Eye, EyeOff } from "lucide-react";

const FONTS = [
  "'Chakra Petch', sans-serif",
  "'Barlow', sans-serif",
  "'Oswald', sans-serif",
  "'Rajdhani', sans-serif",
  "'Sora', sans-serif",
  "'Space Grotesk', sans-serif",
];
const COLORS = [
  { label: "Nova Emerald", v: "156 72% 52%" },
  { label: "Legendary Blue", v: "221 83% 55%" },
  { label: "Esports Red", v: "352 83% 55%" },
  { label: "Neon Green", v: "142 71% 45%" },
  { label: "Royal Purple", v: "265 83% 62%" },
  { label: "Amber", v: "38 92% 50%" },
  { label: "Cyan", v: "190 90% 50%" },
];

export default function AdminSettings() {
  const { refresh, applyTheme } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef();

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);

  if (!s) return <div className="text-white/40">Loading settings…</div>;

  const upd = (patch) => { const next = { ...s, ...patch }; setS(next); applyTheme(next); };
  const updSeo = (patch) => upd({ seo: { ...s.seo, ...patch } });

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd({ logo_url: r.data.url });
      toast.success("Logo uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const addMenu = (key) => {
    const list = [...(s[key] || [])];
    list.push({ id: Math.random().toString(36).slice(2, 10), label: "New Item", path: "/news", enabled: true, order: list.length });
    upd({ [key]: list });
  };
  const updMenu = (key, i, patch) => {
    const list = [...s[key]];
    list[i] = { ...list[i], ...patch };
    upd({ [key]: list });
  };
  const delMenu = (key, i) => {
    const list = s[key].filter((_, idx) => idx !== i);
    upd({ [key]: list });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      await refresh();
      toast.success("Settings saved");
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed (admin only)"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-sm text-white/50">Control branding, typography, navigation, ads, and SEO.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="settings-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-white font-bold disabled:opacity-50">
          <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {/* Branding */}
      <Section title="Branding">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Site Name">
            <input value={s.site_name} onChange={(e) => upd({ site_name: e.target.value })} data-testid="set-site-name" className={inp} />
          </Field>
          <Field label="Tagline">
            <input value={s.tagline} onChange={(e) => upd({ tagline: e.target.value })} className={inp} />
          </Field>
        </div>
        <Field label="Logo Image (optional — overrides text logo)">
          <div className="flex items-center gap-3">
            {s.logo_url && <img src={mediaUrl(s.logo_url)} alt="" className="h-10 bg-white/5 rounded px-2" />}
            <button onClick={() => logoRef.current?.click()} data-testid="set-logo-upload" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 text-white text-sm font-semibold hover:bg-white/20"><Upload size={15} /> Upload Logo</button>
            {s.logo_url && <button onClick={() => upd({ logo_url: "" })} className="text-sm text-red-400 hover:underline">Remove</button>}
            <input ref={logoRef} type="file" accept="image/*" hidden onChange={uploadLogo} />
          </div>
        </Field>
        <Field label="Website Icon (browser tab & Google search)">
          <FaviconField />
        </Field>
      </Section>

      {/* Typography */}
      <Section title="Typography">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Heading Font">
            <select value={s.font_heading} onChange={(e) => upd({ font_heading: e.target.value })} data-testid="set-font-heading" className={inp}>
              {FONTS.map((f) => <option key={f} value={f}>{f.split(",")[0].replace(/'/g, "")}</option>)}
            </select>
          </Field>
          <Field label="Body Font">
            <select value={s.font_body} onChange={(e) => upd({ font_body: e.target.value })} data-testid="set-font-body" className={inp}>
              {FONTS.map((f) => <option key={f} value={f}>{f.split(",")[0].replace(/'/g, "")}</option>)}
            </select>
          </Field>
        </div>
        <Field label={`Base Font Size: ${s.font_size_base}px`}>
          <input type="range" min={13} max={20} value={s.font_size_base} onChange={(e) => upd({ font_size_base: Number(e.target.value) })} data-testid="set-font-size" className="w-full accent-[hsl(var(--primary))]" />
        </Field>
        <div className="mt-2 p-4 rounded-lg bg-black/30 border border-white/10">
          <div className="font-head text-xl font-bold text-white">The quick brown fox — Heading preview</div>
          <div className="text-sm text-white/60 mt-1">Body text preview: jumps over the lazy dog while competing at the highest level.</div>
        </div>
      </Section>

      {/* Colors */}
      <Section title="Theme Colors">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Primary Color">
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button key={c.v} onClick={() => upd({ primary_color: c.v })} title={c.label}
                  className={`w-8 h-8 rounded-full border-2 ${s.primary_color === c.v ? "border-white" : "border-transparent"}`} style={{ background: `hsl(${c.v})` }} />
              ))}
            </div>
          </Field>
          <Field label="Accent Color">
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button key={c.v} onClick={() => upd({ accent_color: c.v })} title={c.label}
                  className={`w-8 h-8 rounded-full border-2 ${s.accent_color === c.v ? "border-white" : "border-transparent"}`} style={{ background: `hsl(${c.v})` }} />
              ))}
            </div>
          </Field>
        </div>
      </Section>

      {/* Navigation */}
      <Section title="Navigation Menu">
        <MenuEditor title="Header Menu" list={s.menus} onAdd={() => addMenu("menus")} onUpd={(i, p) => updMenu("menus", i, p)} onDel={(i) => delMenu("menus", i)} testid="header-menu" />
        <div className="h-4" />
        <MenuEditor title="Footer Menu" list={s.footer_menus} onAdd={() => addMenu("footer_menus")} onUpd={(i, p) => updMenu("footer_menus", i, p)} onDel={(i) => delMenu("footer_menus", i)} testid="footer-menu" />
      </Section>

      {/* AdSense */}
      <Section title="Google AdSense">
        <label className="flex items-center gap-2 cursor-pointer mb-3">
          <input type="checkbox" checked={s.adsense_enabled} onChange={(e) => upd({ adsense_enabled: e.target.checked })} data-testid="set-adsense-enabled" className="w-4 h-4 accent-[hsl(var(--primary))]" />
          <span className="text-sm text-white/80">Enable Google AdSense ad slots</span>
        </label>
        <Field label="AdSense Publisher ID (e.g. ca-pub-1234567890123456)">
          <input value={s.adsense_client} onChange={(e) => upd({ adsense_client: e.target.value })} data-testid="set-adsense-client" className={inp} placeholder="ca-pub-XXXXXXXXXXXXXXXX" />
        </Field>
        <Field label="Google Analytics 4 Measurement ID (e.g. G-XXXXXXXXXX)">
          <input value={s.ga4_measurement_id || ""} onChange={(e) => upd({ ga4_measurement_id: e.target.value.trim() })} data-testid="set-ga4-id" className={inp} placeholder="G-XXXXXXXXXX" />
        </Field>
        <p className="text-xs text-white/40 mt-1">Leave the GA4 ID empty to turn off Google Analytics. Also update <code>/ads.txt</code> in your hosting with your publisher line for full AdSense approval.</p>
      </Section>

      {/* SEO */}
      <Section title="SEO Meta">
        <Field label="Meta Title">
          <input value={s.seo?.meta_title || ""} onChange={(e) => updSeo({ meta_title: e.target.value })} data-testid="set-meta-title" className={inp} />
        </Field>
        <Field label="Meta Description">
          <textarea value={s.seo?.meta_description || ""} onChange={(e) => updSeo({ meta_description: e.target.value })} rows={2} className={inp} />
        </Field>
        <Field label="Meta Keywords">
          <input value={s.seo?.meta_keywords || ""} onChange={(e) => updSeo({ meta_keywords: e.target.value })} className={inp} />
        </Field>
      </Section>
      <div className="mt-10 pt-8 border-t border-white/10" id="homepage-settings"><AdminHomepage /></div>
    </div>
  );
}

const inp = "w-full px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]";

function Section({ title, children }) {
  return (
    <div className="card-surface rounded-xl p-6 mb-5">
      <h2 className="text-base font-bold text-white mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (<div><label className="block text-xs font-semibold text-white/50 mb-1.5">{label}</label>{children}</div>);
}

function MenuEditor({ title, list, onAdd, onUpd, onDel, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-white/50">{title}</span>
        <button onClick={onAdd} data-testid={`add-${testid}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--primary))]"><Plus size={13} /> Add item</button>
      </div>
      <div className="space-y-2">
        {(list || []).map((m, i) => (
          <div key={m.id} className="flex items-center gap-2 p-2 rounded-lg bg-black/30 border border-white/10">
            <GripVertical size={14} className="text-white/20" />
            <input value={m.label} onChange={(e) => onUpd(i, { label: e.target.value })} className="flex-1 bg-transparent text-sm text-white focus:outline-none" placeholder="Label" />
            <input value={m.path} onChange={(e) => onUpd(i, { path: e.target.value })} className="flex-1 bg-transparent text-sm text-white/50 focus:outline-none" placeholder="/path" />
            <button onClick={() => onUpd(i, { enabled: !m.enabled })} className="p-1.5 text-white/50 hover:text-white" title={m.enabled ? "Visible" : "Hidden"}>
              {m.enabled ? <Eye size={15} /> : <EyeOff size={15} />}
            </button>
            <button onClick={() => onDel(i)} className="p-1.5 text-white/50 hover:text-red-400"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
